import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useState } from 'react';
import invariant from 'tiny-invariant';
import type { FiltersProps, IFiltersDef, TFilterKeys } from '../types';
import FiltersDef, { getFiltersLabels } from './Filters/FiltersDef';

const DataTableFilters = ({ filtersDef, onChangeFilter, onSelectedFiltersChange, filtersOptions, favoriteFilters = [], limitOfFilters = 3 }: FiltersProps) => {
  const { t } = useTranslation('common');
  const filterLabels = getFiltersLabels(t);

  invariant(filtersDef, 'data is not defined on DataTableFilters');
  invariant(onChangeFilter, 'onChangeFilter is not defined on DataTableFilters');

  // get filters from data
  const allFilters = FiltersDef.filter((filter) => filtersDef?.some((f) => f.key === filter.key)).sort((a, b) => {
    const indexA = filtersDef.findIndex((f) => f.key === a.key);
    const indexB = filtersDef.findIndex((f) => f.key === b.key);
    return indexA - indexB;
  });

  const fallbackFilters: TFilterKeys[] = allFilters.slice(0, limitOfFilters).map((item) => item.key) || [];

  const activeFilters = filtersDef.filter((filter) => (Array.isArray(filter.value) ? filter.value.length > 0 : filter.value !== undefined));
  const startersFilters = favoriteFilters.length > 0 ? activeFilters.map((filter) => filter.key).concat(favoriteFilters) : fallbackFilters;

  // state to show filters
  const [showFilters, setShowFilters] = useState<TFilterKeys[]>(startersFilters);

  // get filters to render
  const renderFilters = allFilters?.filter((filter) => showFilters.includes(filter.key));

  // get filters to render on dropdown
  const renderFiltersDropdown = allFilters?.filter((filter) => {
    const isVisible = showFilters.includes(filter.key);
    return !isVisible;
  }).length;

  const totalFilters = filtersDef?.length;
  const totalFiltersDropdown = totalFilters - renderFiltersDropdown;
  const labelFilterLength = totalFiltersDropdown === 0 ? t('buttons.filters') : `(${totalFiltersDropdown}/${totalFilters}) ${t('buttons.filters')}`;

  const onClick = useCallback(
    (filter: IFiltersDef) => {
      const newFilters = showFilters.includes(filter.key) ? showFilters.filter((key) => key !== filter.key) : showFilters.concat(filter.key);

      setShowFilters(newFilters);
      onSelectedFiltersChange?.(newFilters);
    },
    [showFilters, onSelectedFiltersChange]
  );

  return (
    <>
      {renderFilters?.map((filter) => {
        const options = filtersOptions?.find((f: any) => f.key === filter.key)?.options;
        return (
          <div data-testid='datatable-filter' key={filter.key} className='flex animate-slide-down-and-fade'>
            {filter.component({
              filters: filtersDef,
              onChangeFilter,
              options: options,
            })}
          </div>
        );
      })}

      {totalFilters > limitOfFilters && (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button variant='outlined' size='sm' color='primary' startIcon={<Icon name='list-filter' />} data-testid='datatable-filters-button'>
              {labelFilterLength}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align='start'>
            {allFilters.map((filter) => (
              <DropdownMenu.CheckboxItem key={filter.key} checked={showFilters.includes(filter.key)} onCheckedChange={() => onClick(filter)}>
                <DropdownMenu.Label>{filterLabels[filter.key]}</DropdownMenu.Label>
              </DropdownMenu.CheckboxItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
    </>
  );
};

export default DataTableFilters;
